import React, { useState } from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { isStringAValidUrl } from '../../../shared/utils/utils-validation-helpers';
import DocumentTitle from '../../../shared/components/DocumentTitle';
import { showNotification, TYPE_ERROR, TYPE_WARNING } from '../../../store/notifications/actions';
import { useLocalContentPerformers } from '../../../api/local-content/collection/product/useLocalContentPerformers';
import { useDispatch } from 'react-redux';
import { LoadingWrapper } from '../../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { LCPerformerPopup } from './LCPerformerPopup';
import ModalDialog from '../../../shared/components/modal/ModalDialog';
import { ModalButtonLine } from '../../../shared/components/modal/ModalButtonLine';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import styled from 'styled-components';
import { useCountries } from '../api/useCountries';
import NoElementsFound, { NO_ELEMENTS_LC_AUTHORS } from '../../../shared/components/NoElementsFound';
import classNames from 'classnames';
import { useDeletePerformer } from '../../../api/local-content/performers/useDeletePerformer';
import { PerformerOut } from '../../../store/api';
import { useUploadLocalContentPerformer } from '../../../api/local-content/collection/product/useUploadLocalContentPerformer';
import { useTranslation } from 'react-i18next';

const filterFields = field => (['array', 'object', 'string'].includes(typeof field) || isNil(field)) && isEmpty(field);

const StHeaderLine = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export const PerformersPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showPerformerPopup, setShowPerformerPopup] = useState(false);
    const [editPerformer, setEditPerformer] = useState({});
    const [performerToDelete, setPerformerToDelete] = useState<PerformerOut | null>(null);
    const [showPerformerDeletionPopup, setShowPerformerDeletionPopup] = useState(false);
    const [showRepublishDialog, setShowRepublishDialog] = useState(false);
    const [isNewPerformer, setIsNewPerformer] = useState(false);
    const [deletePerformer, setDeletePerformer] = useState<any | null>(null);

    const { data: performers, isLoading: isPerformersLoading } = useLocalContentPerformers();
    const { data: countries, isInitialLoading: isCountriesLoading } = useCountries({ enabled: true });
    const { mutate: deletePerformerMutation } = useDeletePerformer();
    const { mutate: savePerformerMutation } = useUploadLocalContentPerformer();

    const getPerformerCountry = (performerCountryCode?: string) => {
        const performerCountry = countries?.find(country => country.code === performerCountryCode);
        return t(`countries:countries.${performerCountry?.code}`, {
            defaultValue: performerCountry?.name
        });
    };

    const loading = isPerformersLoading || isCountriesLoading;
    const performerListClasses = classNames({ 'min-h-screen': isPerformersLoading }, 'lc-performers-list');

    function handleShowPerformerPopup(performer) {
        setEditPerformer(performer);
        setIsNewPerformer(false);
        setShowPerformerPopup(true);
    }

    function handleAddNewPerformer() {
        setEditPerformer({});
        setIsNewPerformer(true);
        setShowPerformerPopup(true);
    }

    function handleShowPerformerDeletionPopup() {
        setShowPerformerDeletionPopup(true);
        setShowPerformerPopup(false);
        setPerformerToDelete(editPerformer);
    }

    function hidePerformerDeletionPopup() {
        setShowPerformerDeletionPopup(false);
        setShowPerformerPopup(true);
        setEditPerformer(deletePerformer);
        setIsNewPerformer(false);
        setDeletePerformer(null);
    }

    function hidePerformerPopup() {
        setShowPerformerPopup(false);
        setEditPerformer({});
        setIsNewPerformer(true);
    }

    function checkForIncompletePerformerData(performer) {
        const mandatoryFields = [
            {
                field: 'lastName',
                name: 'Last Name'
            },
            { field: 'country', name: 'Country' },
            {
                field: 'biography',
                name: 'Biography'
            }
        ];

        return mandatoryFields.filter(field => filterFields(performer[field.field])).map(field => field.name);
    }

    function onSavePerformer(performer, photo) {
        const websiteUrl = performer['website'];
        const incompletePerformerData = checkForIncompletePerformerData(performer);

        if (websiteUrl && !isStringAValidUrl(websiteUrl)) {
            dispatch(showNotification(t('yourLocalContent.yourTitles.warnings.invalidUrl'), TYPE_ERROR, 15000));
            return Promise.reject(0);
        }
        if (incompletePerformerData.length > 0) {
            const invalidFields = incompletePerformerData.join(', ');
            dispatch(
                showNotification(
                    t('yourLocalContent.yourTitles.warnings.incompleteData', { invalidFields: invalidFields }),
                    TYPE_ERROR,
                    15000
                )
            );
            return Promise.reject(0);
        }
        savePerformerMutation(
            { performer, photo },
            {
                onSuccess: data => {
                    if (data.productsChanged) {
                        setShowRepublishDialog(true);
                    }
                    hidePerformerPopup();
                    setIsNewPerformer(false);
                    setShowPerformerPopup(false);
                }
            }
        );
    }

    function onDeletePerformer() {
        const performerProductAmount = performerToDelete?.productAmount ?? 0;
        if (performerProductAmount > 0) {
            dispatch(
                showNotification(
                    t('yourLocalContent.yourAuthors.warnings.deleteWarning', {
                        count: performerProductAmount,
                        firstName: performerToDelete?.firstName || '',
                        lastName: performerToDelete?.lastName || ''
                    }),
                    TYPE_WARNING
                )
            );
            setPerformerToDelete(null);
            setShowPerformerDeletionPopup(false);
            return Promise.reject(0);
        } else {
            deletePerformerMutation(performerToDelete?.id!, {
                onSuccess: () => {
                    setPerformerToDelete(null);
                    setShowPerformerDeletionPopup(false);
                    hidePerformerPopup();
                }
            });
        }
    }

    return (
        <DocumentTitle title={t('components.documentTitle.localContentPerformer')}>
            <div className={performerListClasses} data-displayname="LCPerformerListView">
                <DocumentTitle title={t('components.documentTitle.authors')} />
                <LoadingWrapper>
                    <LoadingOverlay loading={loading} />
                </LoadingWrapper>
                <LCPerformerPopup
                    onAbort={hidePerformerPopup}
                    performer={editPerformer}
                    isNew={isNewPerformer}
                    show={showPerformerPopup}
                    onDelete={handleShowPerformerDeletionPopup}
                    onSuccess={onSavePerformer}
                />
                <ModalDialog
                    show={showRepublishDialog}
                    onCloseButtonClick={() => setShowRepublishDialog(false)}
                    isHeadlineHidden={true}
                >
                    <p>{t('yourLocalContent.yourAuthors.warnings.republish')}</p>
                    <div className="dlb-actions">
                        <a className="lk lk-confirm" onClick={() => setShowRepublishDialog(false)}>
                            {t('yourLocalContent.yourAuthors.close')}
                        </a>
                    </div>
                </ModalDialog>
                <ModalDialog
                    show={showPerformerDeletionPopup}
                    title={t('yourLocalContent.yourAuthors.warnings.deleteAuthor')}
                >
                    <ModalButtonLine>
                        <Button
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.NEUTRAL}
                            onClick={hidePerformerDeletionPopup}
                            text={t('cancel')}
                            className="bt2 modal-button"
                        />
                        <Button
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.DANGER}
                            text={t('yourLocalContent.yourTitles.remove')}
                            onClick={onDeletePerformer}
                            className="bt2 modal-button"
                        />
                    </ModalButtonLine>
                </ModalDialog>
                <StHeaderLine>
                    <Button
                        onClick={handleAddNewPerformer}
                        text={t('yourLocalContent.yourAuthors.createNewAuthor')}
                        size={ButtonSize.LARGE}
                    />
                </StHeaderLine>
                {performers?.length === 0 && !loading ? (
                    <NoElementsFound type={NO_ELEMENTS_LC_AUTHORS} />
                ) : (
                    <table style={{ fontSize: '13px' }}>
                        <thead>
                            <tr>
                                <th>{t('yourLocalContent.yourAuthors.lastName')}</th>
                                <th>{t('yourLocalContent.yourAuthors.firstName')}</th>
                                <th>{t('yourLocalContent.yourAuthors.country')}</th>
                                <th>{t('yourLocalContent.yourAuthors.website')}</th>
                                <th>{t('yourLocalContent.yourAuthors.biography')}</th>
                                <th>{t('yourLocalContent.yourAuthors.products')}</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {performers?.map(performer => (
                                <tr key={performer.id}>
                                    <td>{performer.lastName}</td>
                                    <td>{performer.firstName}</td>
                                    <td>{getPerformerCountry(performer.country ?? '')}</td>
                                    <td>{performer.website}</td>
                                    <td className="lc-biography">{performer.biography}</td>
                                    <td>
                                        {t('numberFormat.numberWithoutDecimalPoint', { val: performer.productAmount })}
                                    </td>
                                    <td>
                                        <a
                                            onClick={() => handleShowPerformerPopup(performer)}
                                            className="bt bt-bordered material-link"
                                            style={{ display: 'inline-flex', fontSize: '12px' }}
                                        >
                                            <i className="material-icons">mode_edit</i>
                                            <span className="label">{t('yourLocalContent.yourAuthors.edit')}</span>
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </DocumentTitle>
    );
};
